import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Input, Row } from 'antd';
import {
  StyledButton,
  StyledColumn,
  StyledFormItem,
  StyledIcon
} from './styled';
import { StyledTable } from '../styled';
import { TitleComponent } from '../../components/TableComponents';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { CustomHeaderV2 } from '../../components/CustomHeaderComponent/customHeader';
import ConfigureDrawer from '../../configureInstanceDashboard/ConfigureDrawer';
import { challengerNameRules, variantsDeselectRule } from './FormRules';

const TypologyRulesConfig = ({
  typologies,
  championTypologies,
  championTypologiesDetails,
  onCreate
}) => {
  const [form] = Form.useForm();
  const drawerRef = useRef();
  const champDrawerRef = useRef();
  const [hasFormError, setHasFormError] = useState(true);
  const [selectedRules, setSelectedRules] = useState([]);
  const [selectedChampionRules, setSelectedChampionRules] = useState([]);

  const typologyRules = useMemo(() => {
    return typologies.reduce((results, typology) => {
      const rules = JSON.parse(typology.rules || '[]')?.map((rule) => ({
        ...rule,
        typologyId: typology.id,
        id: rule.ruleId,
        title: rule.ruleName || rule.name,
        variantName: rule.ruleName || rule.name // variantName required for drawer componnet.
      }));

      return [...results, ...rules];
    }, []);
  }, [typologies]);

  const championTypologyRules = useMemo(() => {
    return championTypologies.map((typology, index) => {
      const rules = JSON.parse(typology.rules || '[]')?.find(
        (item) => item.ruleId === typology.id
      );

      return {
        ...rules,
        ...typology,
        variantName: typology.title // variantName required for drawer componnet.
      };
    });
  }, [championTypologies]);

  const championTypologySets = useMemo(() => {
    return championTypologiesDetails.map((typology) => ({
      ...typology,
      rules:
        championTypologies.find((f) => f.typologyId === typology.id).rules ||
        '[]'
    }));
  }, [championTypologiesDetails]);

  // Select all the rules in default.
  useEffect(() => {
    setSelectedRules(typologyRules.map(({ id }) => id));
  }, [typologyRules]);

  // Select all the rules in default.
  useEffect(() => {
    setSelectedChampionRules(championTypologyRules.map(({ id }) => id));
  }, [championTypologyRules]);

  const onDrawerOpen = (record) => {
    drawerRef.current?.showDrawer(0, record);
  };

  const onChampDrawerOpen = (record) => {
    champDrawerRef.current?.showDrawer(0, record);
  };

  const columns = [
    {
      title: (
        <CustomHeaderV2
          title={`Selected RunInstance Variants (${selectedRules.length})`}
        />
      ),
      key: 'typology',
      render: (record) => (
        <StyledColumn>
          <TitleComponent title={record.title} />
          <StyledIcon
            icon={faInfoCircle}
            onClick={() => onDrawerOpen(record)}
          />
        </StyledColumn>
      )
    }
  ];

  const championcolumns = [
    {
      title: (
        <CustomHeaderV2
          title={`Selected Current Champion Variants (${selectedChampionRules.length})`}
        />
      ),
      width: '95%',
      key: 'typology',
      render: (record) => (
        <StyledColumn>
          <TitleComponent title={record.title} />
          <StyledIcon
            icon={faInfoCircle}
            onClick={() => onChampDrawerOpen(record)}
          />
        </StyledColumn>
      )
    }
  ];

  const rowSelection = useMemo(
    () => ({
      type: 'checkbox',
      selectedRowKeys: selectedRules,
      onChange: (ids) => setSelectedRules(ids),
      preserveSelectedRowKeys: true,
      columnWidth: 48
    }),
    [selectedRules]
  );

  const championRowSelection = useMemo(
    () => ({
      type: 'checkbox',
      selectedRowKeys: selectedChampionRules,
      onChange: (ids) => setSelectedChampionRules(ids),
      preserveSelectedRowKeys: true,
      columnWidth: 48
    }),
    [selectedChampionRules]
  );

  const onCreateClick = () => {
    form
      .validateFields()
      .then((values) => {
        const rules = typologyRules.map(({ typologyId, id, title }) => ({
          typologyId,
          ruleId: id,
          ruleName: title,
          isSelected: selectedRules.includes(id) ? 'true' : 'false'
        }));

        const rulesChampion = championTypologyRules.map(
          ({ typologyId, id, title }) => ({
            typologyId,
            ruleId: id,
            ruleName: title,
            isSelected: selectedChampionRules.includes(id) ? 'true' : 'false'
          })
        );

        onCreate({
          ...values,
          allRules: [...rules, ...rulesChampion]
        });
      })
      .catch((_) => {});
  };

  const onFormValuesChange = () => {
    setHasFormError(
      !form.isFieldsTouched(true) ||
        form.getFieldsError().some((item) => item.errors.length > 0)
    );
  };

  useEffect(() => {
    onFormValuesChange();
  }, [selectedRules]);

  useEffect(() => {
    if (typologyRules.length === selectedRules.length) {
      form.setFieldsValue({ reasonMessage: '' });
    }
  }, [typologyRules, selectedRules]);

  return (
    <Form form={form} layout="vertical" onFieldsChange={onFormValuesChange}>
      <StyledFormItem
        label="Challenger Name"
        name="challengerName"
        rules={challengerNameRules}>
        <Input maxLength={255} placeholder="Please input the challenger name" />
      </StyledFormItem>
      <StyledFormItem label="Please select the variants shown below">
        <Row gutter={32}>
          <Col span={championTypologyRules.length > 0 ? 12 : 24}>
            <StyledTable
              rowKey={({ id }) => id}
              columns={columns}
              dataSource={typologyRules}
              rowSelection={rowSelection}
              pagination={false}
            />
          </Col>
          {championTypologyRules.length > 0 && (
            <Col span={12}>
              <StyledTable
                rowKey={({ id }) => id}
                columns={championcolumns}
                dataSource={championTypologyRules}
                rowSelection={championRowSelection}
                pagination={false}
              />
            </Col>
          )}
        </Row>
      </StyledFormItem>
      {typologyRules.length !== selectedRules.length && (
        <StyledFormItem
          label="Please state your reasons for not selecting the variant(s)"
          name="reasonMessage"
          rules={variantsDeselectRule}>
          <Input maxLength={255} placeholder="Please enter your notes here" />
        </StyledFormItem>
      )}
      <StyledButton
        type="primary"
        onClick={onCreateClick}
        disabled={selectedRules.length === 0 || hasFormError}>
        Create Challenger
      </StyledButton>
      <ConfigureDrawer
        typologies={typologies || []}
        ref={drawerRef}
        isReadOnly={true}
      />
      {championTypologyRules?.length > 0 && (
        <ConfigureDrawer
          typologies={championTypologySets || []}
          ref={champDrawerRef}
          isReadOnly={true}
        />
      )}
    </Form>
  );
};

TypologyRulesConfig.propTypes = {
  typologies: PropTypes.array,
  championTypologies: PropTypes.array,
  championTypologiesDetails: PropTypes.array,
  onCreate: PropTypes.func
};

export default TypologyRulesConfig;
